/**
 * DOM PHI Masker
 *
 * Temporarily masks PHI in the live DOM before screenshot capture.
 * Walks all text nodes, input values, and element attributes, replacing
 * PHI patterns with [REDACTED]. Returns a restore function that puts
 * everything back exactly as it was.
 *
 * This is the primary, deterministic masking layer. OCR-based masking
 * (screenshotPhiMasker.ts) serves as a secondary fallback for PHI
 * rendered inside images/canvas elements that DOM masking can't reach.
 */

import { maskString } from './phiMasking';

// Elements to skip during DOM traversal
const SKIP_TAGS = new Set(['SCRIPT', 'STYLE', 'NOSCRIPT', 'TEMPLATE']);

// Attributes that may contain PHI
const CHECKED_ATTRS = ['title', 'aria-label', 'alt', 'placeholder', 'value'];

interface MaskedTextNode {
  node: Text;
  originalText: string;
}

interface MaskedInput {
  element: HTMLInputElement | HTMLTextAreaElement;
  originalValue: string;
  originalType: string;
}

interface MaskedAttr {
  element: Element;
  attrName: string;
  originalValue: string;
}

interface MaskedLabeled {
  element: HTMLElement;
  textNode: Text | null;
  originalText: string;
}

// Track elements identified as PHI labels so the text node walker can skip them
const _labelElements = new WeakSet<HTMLElement>();

interface MaskedChip {
  element: HTMLElement;
  originalText: string;
}

export interface DomMaskResult {
  restore: () => void;
  maskedCount: number;
  details: {
    textNodes: number;
    inputs: number;
    attributes: number;
    chips: number;
    labeled: number;
    tableColumns: number;
  };
}

// PHI labels that identify value elements to mask
const PHI_LABELS = [
  'patient name', 'patient', 'name',
  'mrn', 'medical record', 'medical record number', 'medical record #',
  'date of birth', 'dob', 'birth date', 'birthday',
  'ssn', 'social security',
  'phone', 'phone number', 'telephone',
  'email', 'e-mail', 'email address',
  'address',
  'gender', 'sex',
  'diagnosis', 'history', 'symptoms',
  'accession', 'accession number',
  'ordering physician', 'physician',
];

/**
 * Check if an element's text matches a PHI label (case-insensitive, trimmed).
 * Only matches pure labels like "Patient Name" or "MRN:", not label+value
 * combinations like "DOB: 01/15/1980" or "MRN: 12345678".
 */
function isPhiLabel(text: string): boolean {
  const normalized = text.trim().toLowerCase();
  if (!normalized) return false;
  // Check exact match first
  if (PHI_LABELS.some((label) => normalized === label)) return true;
  // Check label followed by colon only (e.g., "MRN:", "DOB:")
  // The part after the colon must be empty or just whitespace
  for (const label of PHI_LABELS) {
    if (normalized === label + ':') return true;
  }
  return false;
}

/**
 * Find the value element next to a PHI label element.
 * Looks at siblings and parent's children for the nearest text-bearing element.
 */
function findSiblingValueElement(labelEl: HTMLElement): HTMLElement | null {
  const parent = labelEl.parentElement;
  if (!parent) return null;

  // Strategy 1: Next sibling element
  let next = labelEl.nextElementSibling as HTMLElement | null;
  while (next) {
    const text = next.textContent?.trim();
    if (text && !isPhiLabel(text)) return next;
    next = next.nextElementSibling as HTMLElement | null;
  }

  // Strategy 2: Previous sibling element (some layouts put value before label)
  let prev = labelEl.previousElementSibling as HTMLElement | null;
  while (prev) {
    const text = prev.textContent?.trim();
    if (text && !isPhiLabel(text)) return prev;
    prev = prev.previousElementSibling as HTMLElement | null;
  }

  // Strategy 3: Parent's next sibling (label and value in separate containers)
  let parentNext = parent.nextElementSibling as HTMLElement | null;
  while (parentNext) {
    const text = parentNext.textContent?.trim();
    if (text && !isPhiLabel(text)) return parentNext;
    parentNext = parentNext.nextElementSibling as HTMLElement | null;
  }

  return null;
}

/**
 * Mask values in table columns where the header is a PHI label.
 * Finds th elements with PHI labels, determines column index,
 * and masks all td elements at that index in each row.
 */
function maskTableColumns(maskedLabeled: MaskedLabeled[]): void {
  const tables = document.body.querySelectorAll('table');
  for (const table of tables) {
    if (table.closest('[data-report-issue-widget]')) continue;

    const headerRow = table.querySelector('thead tr');
    if (!headerRow) continue;

    const headerCells = headerRow.querySelectorAll('th');
    const phiColumnIndices: number[] = [];

    headerCells.forEach((th, idx) => {
      const text = th.textContent?.trim() ?? '';
      if (isPhiLabel(text)) {
        phiColumnIndices.push(idx);
      }
    });

    if (phiColumnIndices.length === 0) continue;

    const bodyRows = table.querySelectorAll('tbody tr');
    for (const row of bodyRows) {
      const cells = row.querySelectorAll('td');
      for (const colIdx of phiColumnIndices) {
        const cell = cells[colIdx] as HTMLElement | undefined;
        if (!cell) continue;
        const text = cell.textContent?.trim() ?? '';
        if (!text || text === '—' || text === '-') continue;

        // Check if already masked by text node walker
        const alreadyMasked = maskedLabeled.some((m) => m.element === cell);
        if (alreadyMasked) continue;

        maskedLabeled.push({ element: cell, textNode: null, originalText: cell.textContent ?? '' });
        // Mask all text nodes within the cell to preserve child element structure
        const cellTextNodes = getTextNodes(cell);
        if (cellTextNodes.length > 0) {
          for (const tn of cellTextNodes) {
            const orig = tn.nodeValue ?? '';
            if (orig.trim()) {
              maskedLabeled.push({ element: tn.parentElement ?? cell, textNode: tn, originalText: orig });
              tn.nodeValue = '[REDACTED]';
            }
          }
        } else {
          cell.textContent = '[REDACTED]';
        }
      }
    }
  }
}

/**
 * Mask values next to PHI label elements.
 * Scans for elements whose text matches PHI labels (e.g., "Patient Name",
 * "MRN", "Date of Birth") and masks the adjacent value element.
 */
function maskLabeledValues(maskedLabeled: MaskedLabeled[]): void {
  // Find all elements with short text that could be labels
  const candidates = document.body.querySelectorAll<HTMLElement>(
    'span, div, p, label, td, th, .MuiTypography-root',
  );

  for (const el of candidates) {
    if (el.closest('[data-report-issue-widget]')) continue;

    const text = el.textContent?.trim() ?? '';
    if (!text || text.length > 30) continue; // Labels are short
    if (!isPhiLabel(text)) continue;

    // Skip if this element has child elements — but only check direct
    // element children, not text nodes. A label like <span>MRN <sup>*</sup></span>
    // should still be detected by checking its direct text content.
    const directText = Array.from(el.childNodes)
      .filter((n) => n.nodeType === Node.TEXT_NODE)
      .map((n) => (n as Text).nodeValue ?? '')
      .join('')
      .trim();
    if (!directText || !isPhiLabel(directText)) continue;

    // Mark this element as a label so the text node walker skips it
    _labelElements.add(el);

    // Find the value element next to this label
    const valueEl = findSiblingValueElement(el);
    if (!valueEl) continue;

    const valueText = valueEl.textContent?.trim() ?? '';
    if (!valueText || valueText === '—' || valueText === '-' || valueText === 'N/A') continue;
    if (isPhiLabel(valueText)) continue; // Don't mask other labels

    // Check if already masked
    const alreadyMasked = maskedLabeled.some((m) => m.element === valueEl);
    if (alreadyMasked) continue;

    // Mask all text nodes within the value element (preserves DOM structure)
    const textNodes = getTextNodes(valueEl);
    let didMask = false;
    for (const tn of textNodes) {
      const orig = tn.nodeValue ?? '';
      if (orig.trim()) {
        maskedLabeled.push({ element: tn.parentElement ?? valueEl, textNode: tn, originalText: orig });
        tn.nodeValue = '[REDACTED]';
        didMask = true;
      }
    }
    // Fallback: if no text nodes found, set textContent directly
    if (!didMask) {
      maskedLabeled.push({ element: valueEl, textNode: null, originalText: valueEl.textContent ?? '' });
      valueEl.textContent = '[REDACTED]';
    }
  }
}

/** Get all non-empty text nodes within an element. */
function getTextNodes(root: HTMLElement): Text[] {
  const result: Text[] = [];
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode(node: Text): number {
      const parent = node.parentElement;
      if (!parent || SKIP_TAGS.has(parent.tagName)) return NodeFilter.FILTER_REJECT;
      if (!(node.nodeValue ?? '').trim()) return NodeFilter.FILTER_REJECT;
      return NodeFilter.FILTER_ACCEPT;
    },
  });
  let n: Text | null;
  while ((n = walker.nextNode() as Text | null)) result.push(n);
  return result;
}

/**
 * Mask all PHI in the current document's DOM.
 * Returns a restore function that reverts all changes.
 */
export function maskDomPhi(): DomMaskResult {
  const maskedTextNodes: MaskedTextNode[] = [];
  const maskedInputs: MaskedInput[] = [];
  const maskedAttrs: MaskedAttr[] = [];
  const maskedChips: MaskedChip[] = [];
  const maskedLabeled: MaskedLabeled[] = [];

  // 0. Structural masking: find label-value pairs and mask values
  // Run this FIRST so it catches PHI that regex would miss (standalone names, MRNs, dates)
  maskLabeledValues(maskedLabeled);
  maskTableColumns(maskedLabeled);

  // 1. Walk all text nodes and mask PHI content
  const walker = document.createTreeWalker(
    document.body,
    NodeFilter.SHOW_TEXT,
    {
      acceptNode(node: Text): number {
        const parent = node.parentElement;
        if (!parent || SKIP_TAGS.has(parent.tagName)) {
          return NodeFilter.FILTER_REJECT;
        }
        // Skip our own report widget modal
        if (parent.closest('[data-report-issue-widget]')) {
          return NodeFilter.FILTER_REJECT;
        }
        // Skip elements identified as PHI labels by structural masking
        if (_labelElements.has(parent)) {
          return NodeFilter.FILTER_REJECT;
        }
        const text = node.nodeValue ?? '';
        if (!text.trim()) return NodeFilter.FILTER_REJECT;
        // Check if text contains PHI patterns
        const masked = maskString(text);
        if (masked !== text) {
          return NodeFilter.FILTER_ACCEPT;
        }
        return NodeFilter.FILTER_REJECT;
      },
    },
  );

  let textNode: Text | null;
  while ((textNode = walker.nextNode() as Text | null)) {
    const original = textNode.nodeValue ?? '';
    const masked = maskString(original);
    if (masked !== original) {
      maskedTextNodes.push({ node: textNode, originalText: original });
      textNode.nodeValue = masked;
    }
  }

  // 2. Mask input/textarea values that contain PHI
  const inputs = document.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>(
    'input[type="text"], input[type="search"], input:not([type]), textarea',
  );
  for (const input of inputs) {
    // Skip elements inside our widget
    if (input.closest('[data-report-issue-widget]')) continue;

    const value = input.value;
    if (!value || !value.trim()) continue;

    const masked = maskString(value);
    if (masked !== value) {
      const originalType = input.tagName === 'INPUT' ? input.type : 'textarea';
      maskedInputs.push({ element: input, originalValue: value, originalType });
      input.value = masked;
    }
  }

  // 3. Mask PHI in element attributes
  const allElements = document.body.querySelectorAll('*');
  for (const el of allElements) {
    // Skip our widget
    if (el.closest('[data-report-issue-widget]')) continue;

    for (const attrName of CHECKED_ATTRS) {
      const attrValue = el.getAttribute(attrName);
      if (!attrValue || !attrValue.trim()) continue;

      const masked = maskString(attrValue);
      if (masked !== attrValue) {
        maskedAttrs.push({ element: el, attrName, originalValue: attrValue });
        el.setAttribute(attrName, masked);
      }
    }
  }

  // 4. Mask MUI Chip elements that display PHI (e.g., "MRN: 12345678", "DOB: 01/15/1980")
  // Chips are common in this app for displaying patient info
  const chips = document.body.querySelectorAll<HTMLElement>('.MuiChip-label');
  for (const chip of chips) {
    if (chip.closest('[data-report-issue-widget]')) continue;

    const text = chip.textContent ?? '';
    if (!text.trim()) continue;

    const masked = maskString(text);
    if (masked !== text) {
      maskedChips.push({ element: chip, originalText: text });
      chip.textContent = masked;
    }
  }

  // 5. Mask table cells that contain PHI patterns (patient name, MRN columns)
  const tableCells = document.body.querySelectorAll<HTMLElement>('td, th');
  for (const cell of tableCells) {
    if (cell.closest('[data-report-issue-widget]')) continue;

    const text = cell.textContent ?? '';
    if (!text.trim()) continue;

    const masked = maskString(text);
    if (masked !== text) {
      // Check if we haven't already masked this via text node walking
      const alreadyMasked = maskedTextNodes.some((m) => m.node.parentElement === cell);
      if (!alreadyMasked) {
        // Mask individual text nodes to preserve child element structure
        const cellTextNodes = getTextNodes(cell);
        if (cellTextNodes.length > 0) {
          for (const tn of cellTextNodes) {
            const orig = tn.nodeValue ?? '';
            if (orig.trim()) {
              const nodeMasked = maskString(orig);
              if (nodeMasked !== orig) {
                maskedTextNodes.push({ node: tn, originalText: orig });
                tn.nodeValue = nodeMasked;
              }
            }
          }
        } else {
          maskedChips.push({ element: cell, originalText: text });
          cell.textContent = masked;
        }
      }
    }
  }

  const maskedCount =
    maskedTextNodes.length + maskedInputs.length + maskedAttrs.length +
    maskedChips.length + maskedLabeled.length;

  const restore = () => {
    // Restore text nodes
    for (const { node, originalText } of maskedTextNodes) {
      node.nodeValue = originalText;
    }
    // Restore inputs
    for (const { element, originalValue } of maskedInputs) {
      element.value = originalValue;
    }
    // Restore attributes
    for (const { element, attrName, originalValue } of maskedAttrs) {
      element.setAttribute(attrName, originalValue);
    }
    // Restore chips and table cells
    for (const { element, originalText } of maskedChips) {
      element.textContent = originalText;
    }
    // Restore labeled values (text nodes within value elements)
    for (const { element, textNode, originalText } of maskedLabeled) {
      if (textNode) {
        textNode.nodeValue = originalText;
      } else {
        element.textContent = originalText;
      }
    }
  };

  return {
    restore,
    maskedCount,
    details: {
      textNodes: maskedTextNodes.length,
      inputs: maskedInputs.length,
      attributes: maskedAttrs.length,
      chips: maskedChips.length,
      labeled: maskedLabeled.length,
      tableColumns: maskedLabeled.filter((m) => m.element.tagName === 'TD').length,
    },
  };
}
